import { Link } from 'react-router-dom';
import { ArrowRightIcon, MessageCircleIcon } from 'lucide-react';
import { PressStrip } from '../shared/PressStrip';
import { ProductCard } from '../shared/ProductCard';
import { HeadingText } from '../shared/HeadingText';
import { ParallaxMedia } from '../shared/ParallaxMedia';
import { PageHero } from '../shared/PageHero';
import { CreatorRail } from './CreatorRail';
import { VisitBlock } from '../shared/VisitBlock';
import { placeholderImages } from '../../data/placeholderImages';
import { founder, salon } from '../../data/content';
import { pageActions } from '../../data/pageActions';
import { usePages, useSite } from '../../contexts/SiteContext';
import { policy, policyCopy } from '../../data/policies';
import { shopLabels } from '../../data/shopLabels';
import { mediaOr } from '../../core/rules/media';
import { staffList } from '../../core/rules/salonDays';
import { useOverlayActions } from '../../contexts/StoreContext';
import { useCatalog } from '../../contexts/CatalogContext';
import { fill } from '../../utils/copy';
import { homeBestsellers } from '../home/homeRules';
import { quietLink, sectionHeading } from '../../ui/sections';
import { btnPrimaryLg, btnSecondaryLg } from '../../ui/styles';

/** Zoveel producten van "de plank van Sarah" (twee rijen van drie). */
const SHELF_COUNT = 6;

/** Alleen feiten die Sarah kan onderbouwen; geen opgeklopte getallen. */
const facts = (withColleague: boolean) => [{
  value: policyCopy.experience,
  label: 'in de huidzorg'
},
// Met een collega klopt "altijd door mij" niet meer.
{
  value: 'Eén',
  label: withColleague ? 'cliënt tegelijk, met alle aandacht' : 'cliënt tegelijk, volgens mijn werkwijze'
}, {
  value: `${policy.responseHours} uur`,
  label: 'om persoonlijk te reageren op je bericht'
}];

/**
 * Over Sarah, als één verhaal (docs/VORMGEVING.md §3): wie (hero, sand) · haar verhaal en feiten
 * (sand) · waar ze voor staat (klei) · wie haar aanraden (pers, shell; vaste cliënten, sand) ·
 * wat ze zelf gebruikt (shell, schuifbaar) · kom kennismaken (blush).
 *
 * Collega's staan niet hier maar op de salonpagina, waar je kiest bij wie je komt. Adres en alle
 * openingstijden staan op Contact; hier één regel wanneer je terecht kunt.
 */
export function About() {
  const pages = usePages();
  const site = useSite();
  const {
    openBooking
  } = useOverlayActions();
  const {
    products
  } = useCatalog();
  const copy = pages.about;
  // Feiten moeten kloppen, ook als de collega (nog) niet op de site staat.
  const withColleague = staffList(site.staff).some(s => s.role === 'staff' && s.active);
  const shelf = homeBestsellers(products, SHELF_COUNT);
  const portrait = mediaOr(copy.hero.media, {
    image: founder.portraitPalm,
    alt: `${founder.name}, ${founder.role}`,
    focus: {
      x: 50,
      y: 30
    }
  });
  const atWork = mediaOr(copy.philosophy.media, {
    image: founder.atWork,
    alt: `${founder.name} tijdens een behandeling`
  });
  // De eerste alinea draagt de hero; de rest leest eronder rustig verder.
  const [lead, ...rest] = copy.hero.paragraphs;
  return <>
      {/* Hero: zelfde opbouw als salon en protocollen; het eerste scherm is het portret en haar belofte. */}
      <PageHero titleId="about-title" eyebrow={copy.hero.eyebrow ? fill(copy.hero.eyebrow) : undefined} title={<HeadingText heading={copy.hero.heading} data-id="element-5549" data-mp="2ysd0pjgsw96o" />} body={lead ? fill(lead) : undefined} media={portrait} byline={{
      lead: <span className="text-ink" data-id="element-5550" data-mp="2tubit1v42fws">{founder.name}</span>,
      sub: `${founder.role} · ${salon.city}`
    }} actions={/* Wie haar vertrouwt, wil zien wat ze kiest: de collectie is de hoofdknop, de afspraak de tweede. */
    <>
            <Link to="/webshop" className={btnPrimaryLg} data-id="element-5551" data-mp="2jwxg05241pmy">
              {pageActions.viewCollection}
            </Link>
            {copy.hero.cta && <button type="button" onClick={() => openBooking()} aria-haspopup="dialog" className={btnSecondaryLg} data-id="element-5552" data-mp="i2ptdw8ikb16">
                {fill(copy.hero.cta)}
              </button>}
          </>} data-id="element-5548" data-mp="37lgmcf1lnsce" />

      {/* Het verhaal, en de feiten die ze kan onderbouwen. De feiten komen één voor één binnen. */}
      <section className="bg-sand pb-20 sm:pb-24 lg:pb-28" aria-label={`Over ${founder.firstName}`} data-id="element-5553" data-mp="q3x5wrcdiwzp">
        <div className="mx-auto grid max-w-content gap-12 border-t border-ink/15 px-6 pt-12 lg:grid-cols-12 lg:gap-16 lg:px-10 lg:pt-16" data-id="element-5554" data-mp="c44k9afpf6yl">
          {rest.length > 0 && <div className="max-w-2xl space-y-5 text-[17px] leading-relaxed text-muted lg:col-span-7" data-id="element-5555" data-mp="2dtcfcswgdmf">
              {rest.map((p, i) => <p key={`${i}-${p.slice(0, 24)}`} className={i === 0 ? 'text-ink' : undefined} data-id="element-5556" data-mp="77nu6aoy2iiv" data-mpi={i}>
                  {fill(p)}
                </p>)}
            </div>}
          {/* Getal + uitleg lezen als één zin: "25 jaar in de huidzorg". */}
          <ul className={`grid gap-6 sm:grid-cols-3 lg:grid-cols-1 lg:gap-0 lg:divide-y lg:divide-ink/15 ${rest.length > 0 ? 'lg:col-span-5' : 'lg:col-span-12'}`} data-id="element-5557" data-mp="3fmpdxbdxzq82">
            {facts(withColleague).map(f => <li key={f.label} className="lg:flex lg:items-baseline lg:gap-5 lg:py-5 lg:first:pt-0" data-id="element-5558" data-mp="30z7eosj5fnt1">
                <span className="block type-sub text-ink lg:w-40 lg:shrink-0" data-id="element-5559" data-mp="2w1ffddg6czet">{f.value}</span>
                <span className="mt-1.5 block text-[14px] leading-snug text-muted lg:mt-0" data-id="element-5560" data-mp="3a0x7a1orosse">{f.label}</span>
              </li>)}
          </ul>
        </div>
      </section>

      {/* Waar ik voor sta: het ene klei-vlak. De foto beweegt licht binnen zijn kader. */}
      <section id="filosofie" className="scroll-mt-24 bg-clay text-shell" aria-label="Filosofie" data-id="element-5561" data-mp="3exfagx4g8j2m">
        <div className="grid lg:grid-cols-12" data-id="element-5562" data-mp="3tmbe8hzh39xf">
          <figure className="flex flex-col justify-center px-6 py-20 sm:px-10 lg:col-span-7 lg:py-28 lg:pl-10 lg:pr-16 xl:pl-[max(2.5rem,calc((100vw-80rem)/2+2.5rem))]" data-id="element-5563" data-mp="3tp5zucu830sf">
            <p className="text-[14px] text-shell" data-id="element-5564" data-mp="6j6mk2cvgggs">{fill(copy.philosophy.eyebrow)}</p>
            <blockquote className="mt-5 max-w-2xl type-section" data-id="element-5565" data-mp="1klpucwtwex3">
              “{fill(copy.philosophy.claim)}.”
            </blockquote>
            <p className="mt-8 max-w-xl text-[17px] leading-relaxed text-shell" data-id="element-5566" data-mp="2v9fnl86qhff9">{fill(copy.philosophy.body)}</p>
            <figcaption className="mt-8 type-title italic text-blush" data-id="element-5567" data-mp="34c8dp846jl3i">{founder.firstName}</figcaption>
          </figure>
          <ParallaxMedia media={atWork} sizes="(min-width: 1024px) 42vw, 100vw" range={40} className="aspect-[4/5] w-full sm:aspect-[16/11] lg:col-span-5 lg:aspect-auto lg:h-full lg:min-h-[640px]" data-id="element-5568" data-mp="393gn445if6go" />
        </div>
      </section>

      <PressStrip data-id="element-5569" data-mp="3o07midf6oi6a" />

      <CreatorRail data-id="element-5570" data-mp="3nzxy2u9sej2p" />

      {/* Haar eigen plank: dezelfde rail als op de homepage. */}
      {shelf.length > 0 && <section className="bg-shell py-20 sm:py-24 lg:py-32" aria-labelledby="about-shelf" data-id="element-5571" data-mp="3s0hufvmjdins">
          <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-5572" data-mp="1tn82m83rt1t7">
            <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-5573" data-mp="1xni8zlvh5u8l">
              <div className="max-w-2xl" data-id="element-5574" data-mp="1xpl8qxmfj1qi">
                <h2 id="about-shelf" className={`${sectionHeading} text-ink`} data-id="element-5575" data-mp="1q4iote06vyo6">
                  <HeadingText heading={copy.shelf.heading} data-id="element-5576" data-mp="1ldsju1vodxu0" />
                </h2>
                {copy.shelf.body && <p className="mt-6 text-[17px] leading-relaxed text-muted" data-id="element-5577" data-mp="1ge65fp442bjm">{fill(copy.shelf.body)}</p>}
              </div>
              {copy.shelf.cta && <Link to="/webshop" className={`${quietLink} text-ink`} data-id="element-5578" data-mp="1ceoysscy78cv">
                  {fill(copy.shelf.cta)}
                  <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-5579" data-mp="290qxkrseuro8" />
                </Link>}
            </div>

            {/* Een stil raster: het ene bewegende moment van deze pagina is de filosofie. */}
            <ul aria-label={`De plank van ${founder.firstName}`} className="mt-10 grid grid-cols-2 gap-x-6 gap-y-10 lg:mt-12 lg:grid-cols-3" data-id="element-5580" data-mp="1v456q0qyq33b">
              {shelf.map(p => <li key={p.id} className="h-full" data-id="element-5581" data-mp="jogjd89sr21z">
                  <ProductCard product={p} data-id="element-5582" data-mp="jmvkx7kd2yyu" />
                </li>)}
            </ul>
          </div>
        </section>}

      {/* Kom kennismaken: het gedeelde "kom langs"-blok. Eén knop; de salonpagina en WhatsApp als rustige links. */}
      <VisitBlock titleId="about-salon" eyebrow={copy.salon.eyebrow} heading={copy.salon.heading} body={copy.salon.body} cta={copy.salon.cta} media={{
      image: placeholderImages.salonRoom,
      alt: 'Sfeerbeeld: een rustige behandelkamer met linnen in daglicht'
    }} motion="still" links={<>
            {copy.salon.salonCta && <Link to="/behandelingen" className={`${quietLink} text-ink`} data-id="element-5584" data-mp="2srwan777gjf8">
                {fill(copy.salon.salonCta)}
                <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-5585" data-mp="2xowfxfw8di5a" />
              </Link>}
            {copy.salon.secondaryCta && <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={`${quietLink} text-ink`} data-id="element-5586" data-mp="3q70f73ighon2">
                <MessageCircleIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-5587" data-mp="1eqq337qsmrpc" />
                {fill(copy.salon.secondaryCta)}
                <span className="sr-only" data-id="element-5588" data-mp="19s6j3cvrfpzm">{shopLabels.viaWhatsApp}</span>
              </a>}
          </>} data-id="element-5583" data-mp="29ss1zzjksro3" />
    </>;
}